import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'

import {isCaptchaError} from './errors'
import {MAX_CAPTCHA_RETRIES} from './constants'

export class CaptchaCancelledError extends Error {
  constructor() {
    super('Captcha dialog was closed by user')
    this.name = 'CaptchaCancelledError'
  }
}

export const isCaptchaCancelledError = (error: any): boolean => {
  return error instanceof CaptchaCancelledError
}

export type CaptchaRetryOptions = {
  maxAttempts?: number
  onCaptchaRequired?: () => void
  onCaptchaCompleted?: () => void
}

export const withCaptchaRetry = async <T>(
  flowAPI: ControllerFlowAPI,
  operation: (captchaToken?: string) => Promise<T>,
  options: CaptchaRetryOptions = {},
): Promise<T | undefined> => {
  const {maxAttempts = MAX_CAPTCHA_RETRIES, onCaptchaRequired, onCaptchaCompleted} = options

  try {
    return await operation()
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error)

    if (!isCaptchaError(resolvedError)) {
      throw error
    }
  }

  onCaptchaRequired?.()

  let attempts = 0

  while (attempts < maxAttempts) {
    attempts++

    try {
      const captchaToken =
        await flowAPI.controllerConfig.wixCodeApi.authentication.openCaptchaChallenge()

      onCaptchaCompleted?.()

      return await operation(captchaToken)
    } catch (retryError) {
      if (retryError === undefined) {
        throw new CaptchaCancelledError()
      }
      const retryResolvedError = flowAPI.errorHandler.getResolvedError(retryError)

      if (!isCaptchaError(retryResolvedError) || attempts >= maxAttempts) {
        throw retryError
      }
    }
  }

  throw new Error('CAPTCHA retry attempts exhausted')
}
